"use client";

import React, { useEffect, useState } from "react";
import { FileIcon, FileText, Film, ImageIcon, X } from "lucide-react";
import { motion } from "motion/react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export interface FilePreviewProps {
  file: File;
  index: number;
  onRemove: (index: number) => void;
  className?: string;
  isValid?: boolean;
  validationError?: string;
}

export function FilePreview({
  file,
  index,
  onRemove,
  className,
  isValid = true,
  validationError,
}: FilePreviewProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // Create blob URL for preview
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);

      setPreviewUrl(url);

      // Cleanup blob URL when component unmounts or file changes
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [file]);

  // Handle video load errors
  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    console.warn("Video preview failed to load:", e);
  };

  // Determine file type
  const isImage = file.type.startsWith("image/");
  const isVideo = file.type.startsWith("video/");
  const isPdf = file.type.includes("pdf");
  const isDocument = file.type.includes("document") || file.type.includes("text/");

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";

    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // Render file icon based on type
  const renderFileIcon = () => {
    if (isImage) return <ImageIcon className="h-4 w-4" />;
    if (isVideo) return <Film className="h-4 w-4" />;
    if (isPdf || isDocument) return <FileText className="h-5 w-5" />;

    return <FileIcon className="h-4 w-4" />;
  };

  return (
    <motion.div
      animate={{ opacity: 1, scale: 1, x: 0 }}
      className={cn(
        "flex max-w-full items-center gap-2 rounded-md p-2 text-xs sm:max-w-sm sm:min-w-64",
        isValid ? "bg-primary/10" : "border border-red-700/50 bg-red-900/80 text-red-50",
        className,
      )}
      exit={{ opacity: 0, scale: 0.9, x: -10 }}
      initial={{ opacity: 0, scale: 0.9, x: -10 }}
      transition={{
        duration: 0.2,
        type: "spring",
        stiffness: 500,
        damping: 25,
      }}
    >
      {/* Preview thumbnail */}
      <div className="flex-shrink-0">
        {isImage && previewUrl ? (
          <div className="relative h-8 w-8 overflow-hidden rounded-md">
            <div
              className={cn(
                "bg-muted/20 flex items-center justify-center rounded-md backdrop-blur-sm transition-opacity",
                isImageLoaded ? "h-0 opacity-0" : "h-8 w-8 opacity-100",
              )}
            >
              <ImageIcon className="text-muted-foreground/50 h-4 w-4" />
            </div>
            <img
              alt={file.name}
              className={cn(
                "h-8 w-8 object-cover transition-opacity",
                isImageLoaded ? "opacity-100" : "opacity-0",
              )}
              src={previewUrl}
              onLoad={() => setIsImageLoaded(true)}
            />
          </div>
        ) : isVideo && previewUrl ? (
          <div className="relative h-8 w-8 overflow-hidden rounded-md bg-black/20">
            <video
              muted
              playsInline
              className="h-8 w-8 object-cover"
              controls={false}
              preload="metadata"
              onError={handleVideoError}
            >
              <source src={previewUrl} type={file.type} />
            </video>
            <div className="absolute bottom-0.5 left-0.5 rounded bg-black/70 px-1 py-0.5 text-[8px] text-white">
              <Film className="mr-0.5 inline h-2 w-2" />
            </div>
          </div>
        ) : (
          <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-md">
            {renderFileIcon()}
          </div>
        )}
      </div>

      {/* File info */}
      <div className="min-w-0 flex-1">
        <div className="max-w-[200px] truncate font-medium">{file.name}</div>
        <div className="text-muted-foreground text-[10px] whitespace-nowrap">
          {formatFileSize(file.size)}
        </div>
        {!isValid && validationError && (
          <div className="mt-1 text-[10px] text-red-200">{validationError}</div>
        )}
      </div>

      {/* Remove button */}
      <Button
        className="ml-1 h-4 w-4 flex-shrink-0 rounded-full p-0"
        size="icon"
        variant="ghost"
        onClick={() => onRemove(index)}
      >
        <X className="h-2.5 w-2.5" />
      </Button>
    </motion.div>
  );
}
